<template>
  <div class="upscale-view">
    <v-container>
      <div class="upload-column">
        <v-row>
          <v-col cols="12">
            <h1 class="text-h4 mb-1">Make your AI image look real</h1>
            <p class="text-subtitle-1 upscale-subtitle">
              Generative Detail Reconstruction re-renders your AI image as a
              photograph - real skin, real light, real texture - at up to 4x.
            </p>
          </v-col>
        </v-row>

        <!-- Back from a pack checkout: the paid-for conversion resumes by
             itself (credits arrive via the payment webhook moments later). -->
        <v-alert v-if="resumingPurchase" type="success" outlined class="mb-6">
          <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
          Payment received - finishing your conversion&hellip;
        </v-alert>

        <!-- Back from a pack bought on the result page: nothing is parked,
             so just confirm and invite the next image. -->
        <v-alert v-else-if="creditsAdded" type="success" outlined dismissible class="mb-6">
          <template v-if="creditsArrived">
            Payment received - your new conversions are ready. Upload your
            next image below.
          </template>
          <template v-else>
            <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
            Payment received - adding your conversions&hellip;
          </template>
        </v-alert>

        <!-- Out of free conversions and credits: say so BEFORE an upload,
             not only after a refused submit. -->
        <v-alert
          v-else-if="outOfFunds && !outOfCreditsMessage"
          type="info"
          outlined
          class="mb-6"
        >
          You're out of free conversions. One-time packs start at $5 - no
          subscription needed.
          <div class="mt-2">
            <v-btn small color="primary" @click="showSubscribeDialog = true">
              Get more conversions
            </v-btn>
          </div>
        </v-alert>

        <v-card outlined class="mb-6">
          <v-card-text>
            <!-- Dropzone -->
            <div
              class="dropzone"
              :class="{ 'dropzone--active': dragActive, 'dropzone--has-file': !!previewUrl }"
              @click="!uploading && $refs.fileInput.click()"
              @dragover.prevent="dragActive = true"
              @dragleave.prevent="dragActive = false"
              @drop.prevent="onDrop"
            >
              <input
                ref="fileInput"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                class="dropzone-input"
                :disabled="uploading"
                @change="onFileInputChange"
              />
              <template v-if="batchFiles.length">
                <v-icon size="48" color="primary">mdi-image-multiple-outline</v-icon>
                <div class="dropzone-text mt-3">
                  {{ batchFiles.length }} images selected
                </div>
                <div class="dropzone-hint">
                  All convert with the settings below
                </div>
              </template>
              <template v-else-if="previewUrl">
                <img :src="previewUrl" class="dropzone-preview" alt="Selected image preview" />
              </template>
              <template v-else>
                <v-icon size="48" color="primary">mdi-cloud-upload-outline</v-icon>
                <div class="dropzone-text mt-3">
                  Drop an image here, or click to browse
                </div>
                <div class="dropzone-hint">
                  JPEG, PNG or WebP - up to 25MB · pick several to batch convert
                </div>
              </template>
            </div>
            <div v-if="fileError" class="error-text mt-2">{{ fileError }}</div>

            <!-- Batch queue: one row per image, updated as each submits -->
            <div v-if="batchFiles.length" class="batch-list mt-3">
              <div
                v-for="(entry, index) in batchFiles"
                :key="index"
                class="batch-row"
              >
                <span class="batch-name">{{ entry.file.name }}</span>
                <v-chip
                  x-small
                  :color="batchChipColor(entry.status)"
                  :text-color="entry.status === 'ready' ? undefined : 'white'"
                >
                  {{ batchStatusLabel(entry.status) }}
                </v-chip>
              </div>
            </div>

            <!-- Realism level: the product's primary control -->
            <div class="mt-6">
              <label class="text-subtitle-2">Realism</label>
              <div class="realism-grid mt-2">
                <div
                  v-for="level in realismLevels"
                  :key="level.value"
                  class="realism-card"
                  :class="{
                    'realism-card--selected': realism === level.value,
                    'realism-card--disabled': uploading
                  }"
                  @click="!uploading && (realism = level.value)"
                >
                  <div class="d-flex align-center">
                    <v-icon
                      small
                      :color="realism === level.value ? 'primary' : undefined"
                    >
                      {{ level.icon }}
                    </v-icon>
                    <span class="realism-card-title ml-2">{{ level.title }}</span>
                    <v-chip
                      v-if="level.recommended"
                      x-small
                      outlined
                      color="primary"
                      class="ml-2"
                    >
                      Recommended
                    </v-chip>
                    <!-- Paid feature: any pack or plan unlocks it -->
                    <v-chip
                      v-if="level.value === 'extreme' && !premiumUnlocked"
                      x-small
                      color="warning"
                      outlined
                      class="ml-2"
                    >
                      <v-icon left x-small>mdi-lock</v-icon>
                      Paid
                    </v-chip>
                  </div>
                  <div class="realism-card-desc mt-1">{{ level.desc }}</div>
                </div>
              </div>
            </div>

            <!-- Output size: secondary -->
            <div class="mt-5 d-flex align-center">
              <span class="output-size-label mr-3">Output size</span>
              <v-btn-toggle
                v-model="scale"
                mandatory
                dense
                color="primary"
                :disabled="uploading"
              >
                <v-btn small :value="2">2x</v-btn>
                <v-btn small :value="4">
                  4x
                  <v-icon v-if="!premiumUnlocked" x-small right>mdi-lock</v-icon>
                </v-btn>
              </v-btn-toggle>
              <span v-if="!premiumUnlocked" class="premium-hint ml-3">
                Extreme &amp; 4x unlock with any pack or plan
              </span>
            </div>

            <!-- Advanced settings -->
            <v-expansion-panels
              v-model="advancedOpen"
              flat
              class="advanced-panels mt-5"
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="px-0">
                  <div class="d-flex align-center">
                    <v-icon small left>mdi-tune</v-icon>
                    <span class="text-subtitle-2">Advanced settings</span>
                    <v-chip
                      v-if="advancedSummary"
                      x-small
                      outlined
                      class="ml-2"
                    >
                      {{ advancedSummary }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="advanced-content">
                  <div>
                    <label class="text-subtitle-2">Enhancement strength</label>
                    <v-btn-toggle
                      v-model="enhancementStrength"
                      mandatory
                      dense
                      color="primary"
                      class="mt-1 d-block"
                      :disabled="uploading"
                    >
                      <v-btn value="auto">Auto</v-btn>
                      <v-btn value="low">Low</v-btn>
                      <v-btn value="medium">Medium</v-btn>
                      <v-btn value="high">High</v-btn>
                    </v-btn-toggle>
                    <div class="option-hint mt-1">
                      Auto lets the engine pick the best strength for your image.
                    </div>
                  </div>

                  <div class="mt-4">
                    <v-switch
                      v-model="faceEnhancement"
                      label="Face enhancement"
                      hide-details
                      dense
                      class="mt-0"
                      :disabled="uploading"
                    />
                    <template v-if="faceEnhancement">
                      <div class="mt-3">
                        <label class="text-caption">
                          Face enhancement strength: {{ faceStrength.toFixed(2) }}
                        </label>
                        <v-slider
                          v-model="faceStrength"
                          min="0"
                          max="1"
                          step="0.05"
                          hide-details
                          dense
                          :disabled="uploading"
                        />
                      </div>
                      <div class="mt-2">
                        <label class="text-caption">
                          Face creativity: {{ faceCreativity.toFixed(2) }}
                        </label>
                        <v-slider
                          v-model="faceCreativity"
                          min="0"
                          max="1"
                          step="0.05"
                          hide-details
                          dense
                          :disabled="uploading"
                        />
                      </div>
                    </template>
                  </div>

                  <div class="mt-4">
                    <label class="text-subtitle-2">Output format</label>
                    <v-btn-toggle
                      v-model="outputFormat"
                      mandatory
                      dense
                      color="primary"
                      class="mt-1 d-block"
                      :disabled="uploading"
                    >
                      <v-btn value="jpeg">JPEG</v-btn>
                      <v-btn value="png">PNG</v-btn>
                    </v-btn-toggle>
                  </div>

                  <v-switch
                    v-model="cropToFill"
                    label="Crop to fill the target size"
                    hide-details
                    dense
                    class="mt-4"
                    :disabled="uploading"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Cost estimate -->
            <div class="mt-4 estimate-row">
              <span v-if="estimating" class="text-caption">
                <v-progress-circular indeterminate size="14" width="2" color="primary" />
                Estimating cost&hellip;
              </span>
              <span v-else-if="estimateInfo && estimateInfo.trial" class="text-body-2 trial-note">
                <v-icon small color="success">mdi-gift-outline</v-icon>
                <strong>This conversion is free.</strong>
                {{ freeLeftText }}
              </span>
              <span v-else-if="estimateInfo" class="text-body-2">
                <span v-if="estimateInfo.allowance && Number(estimateInfo.allowance.reserved) > 0">
                  Your free conversion is reserved for work in progress.
                </span>
                <strong>Cost:</strong> {{ estimateInfo.credits }} credit{{ estimateInfo.credits === 1 ? "" : "s" }}
                <a
                  v-if="!estimateInfo.affordable"
                  class="estimate-short"
                  @click.prevent="showSubscribeDialog = true"
                >
                  · get more conversions
                </a>
              </span>
            </div>
            <div v-if="batchFiles.length && estimateInfo" class="text-caption">
              Shown for the first image - each image is priced by its own size
              before it uploads.
            </div>

            <v-btn
              color="primary"
              class="mt-4"
              :disabled="uploading"
              :loading="uploading"
              @click="uploadAndRun"
            >
              <v-icon left>mdi-auto-fix</v-icon>
              {{ convertLabel }}
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Out of credits (job refused at submit - nothing is running) -->
        <v-alert v-if="outOfCreditsMessage" type="warning" outlined class="mb-6">
          {{ outOfCreditsMessage }}
          <div class="mt-2">
            <v-btn small color="primary" to="/subscribe">View plans</v-btn>
            <v-btn
              v-if="parkedImageId"
              small
              text
              class="ml-2"
              :loading="retrying"
              @click="retryParked"
            >
              Retry now
            </v-btn>
          </div>
        </v-alert>

        <!-- Failure -->
        <v-alert v-if="failureMessage" type="error" outlined class="mb-6">
          {{ failureMessage }}
        </v-alert>
      </div>
    </v-container>

    <SubscribeDialog v-model="showSubscribeDialog" :resume-image-id="parkedImageId" />
  </div>
</template>

<script>
import { apiClient } from "@shared/services/api";
import { workFundingHeaders } from "@shared/utils/workBilling";
import SubscribeDialog from "../components/SubscribeDialog.vue";

// Each realism level defaults its output size (still user-overridable).
// Basic/High default to 2x (the affordable core product); Extreme is the
// premium tier and defaults to 4x. 4x and Extreme are paid features: any
// purchase unlocks them (they then cost credits like any conversion).
const LEVEL_DEFAULT_SCALE = { none: 2, basic: 2, high: 2, extreme: 4 };

// Module-level so a "Process another image" round-trip (or any navigation
// within the session) keeps the user's last choices.
const REALISM_LEVELS = [
  {
    value: "none",
    title: "Resolution only",
    icon: "mdi-arrow-expand-all",
    desc: "GDR off - pixels enlarged, nothing rebuilt"
  },
  {
    value: "basic",
    title: "Basic",
    icon: "mdi-image-filter-black-white",
    desc: "A subtle realism touch-up"
  },
  {
    value: "high",
    title: "High",
    icon: "mdi-camera",
    desc: "Full realistic re-render",
    recommended: true
  },
  {
    value: "extreme",
    title: "Extreme",
    icon: "mdi-flare",
    desc: "Maximum photorealism, hyper-detailed"
  }
];

const savedSettings = {
  scale: LEVEL_DEFAULT_SCALE.high,
  realism: "high",
  enhancementStrength: "auto",
  faceEnhancement: true,
  faceStrength: 0.8,
  faceCreativity: 0,
  outputFormat: "jpeg",
  cropToFill: false
};

export default {
  name: "UpscaleView",
  components: { SubscribeDialog },
  data() {
    return {
      selectedFile: null,
      batchFiles: [],
      previewUrl: null,
      dragActive: false,
      fileError: null,

      realismLevels: REALISM_LEVELS,
      scale: savedSettings.scale,
      realism: savedSettings.realism,
      enhancementStrength: savedSettings.enhancementStrength,
      faceEnhancement: savedSettings.faceEnhancement,
      faceStrength: savedSettings.faceStrength,
      faceCreativity: savedSettings.faceCreativity,
      outputFormat: savedSettings.outputFormat,
      cropToFill: savedSettings.cropToFill,
      advancedOpen: null,
      estimateInfo: null,
      estimating: false,
      srcWidth: null,
      srcHeight: null,
      showSubscribeDialog: false,

      uploading: false,
      retrying: false,
      // Back from a pack checkout with a parked upload: auto-resume state.
      resumingPurchase: false,
      // Back from a pack bought on the result page (nothing parked).
      creditsAdded: false,
      creditsArrived: false,
      // Set when a submit was refused with 402: the upload is preserved
      // server-side as needs_credits, so "Retry now" can resume it.
      parkedImageId: null,

      outOfCreditsMessage: null,
      failureMessage: null
    };
  },
  computed: {
    convertLabel() {
      if (this.batchFiles.length) {
        return "Convert " + this.batchFiles.length + " images";
      }
      return this.realism === "none" ? "Upscale image" : "Make it realistic";
    },
    // How many free conversions are left, from the estimate's allowance
    // preview (counted before this one is used).
    // 4x output and Extreme realism are paid features: having credits (any
    // pack or plan grants them) unlocks everything. Mirrors the server-side
    // trial-eligibility rule; the server stays the real gate.
    premiumUnlocked() {
      const user = this.$store.getters.currentUser;
      if (!user || user.is_anonymous) return false;
      const balance = this.$store.getters.creditBalance;
      return !!(balance && Number(balance.balance) > 0);
    },
    // Neither a free conversion nor enough credits for the current
    // settings: surface the offer before an upload, not only after a 402.
    outOfFunds() {
      return !!(
        this.estimateInfo &&
        !this.estimateInfo.trial &&
        !this.estimateInfo.affordable
      );
    },
    freeLeftText() {
      const allowance = this.estimateInfo && this.estimateInfo.allowance;
      const left = allowance ? Math.floor(Number(allowance.remaining) || 0) : 0;
      if (left > 1) return `You have ${left} free conversions left.`;
      if (left === 1) return "This is your last free one.";
      return "";
    },
    // Non-default advanced choices, surfaced on the collapsed accordion so
    // settings left over from a previous run stay visible.
    advancedSummary() {
      const parts = [];
      if (this.enhancementStrength !== "auto") {
        parts.push(
          this.enhancementStrength.charAt(0).toUpperCase() +
            this.enhancementStrength.slice(1) +
            " strength"
        );
      }
      if (!this.faceEnhancement) {
        parts.push("Faces off");
      } else if (this.faceStrength !== 0.8 || this.faceCreativity !== 0) {
        parts.push("Custom faces");
      }
      if (this.outputFormat === "png") parts.push("PNG");
      if (this.cropToFill) parts.push("Crop");
      return parts.length ? parts.join(" · ") : null;
    }
  },
  watch: {
    scale(value, previous) {
      // 4x is a paid feature: without credits, picking it opens the offer
      // and the toggle snaps back (the server enforces the same rule).
      if (value === 4 && !this.premiumUnlocked) {
        // Vuetify's btn-toggle keeps internal state; reverting inside the
        // same tick is swallowed, so snap back on the next one.
        this.$nextTick(() => {
          this.scale = previous === 4 ? 2 : previous;
        });
        this.showSubscribeDialog = true;
        return;
      }
      this.fetchEstimate();
    },
    realism(level, previous) {
      // Extreme is a paid feature too - same treatment as 4x.
      if (level === "extreme" && !this.premiumUnlocked) {
        this.realism = previous === "extreme" ? "high" : previous;
        this.showSubscribeDialog = true;
        return;
      }
      // Each level has its own default output size; the size toggle stays
      // adjustable afterwards. The scale watcher refetches the estimate;
      // refetch here too for the no-change case (e.g. none → basic).
      const preset = LEVEL_DEFAULT_SCALE[level] || 2;
      if (this.scale !== preset) {
        this.scale = preset;
      } else {
        this.fetchEstimate();
      }
    }
  },
  created() {
    this.fetchEstimate();
    // Landing-page handoff for the out-of-allowance case: the landing's
    // converter got a 402 and sends the visitor here to pick a plan, not
    // to try (and fail) another upload.
    if (this.$route.query.subscribe) {
      this.showSubscribeDialog = true;
      this.$router.replace({ path: this.$route.path }).catch(() => {});
      return;
    }
    // Back from a pack checkout (success URL carries the parked image id):
    // finish the conversion the buyer just paid for.
    const purchased = Number(this.$route.query.purchased || 0);
    if (purchased > 0) {
      this.parkedImageId = purchased;
      this.$router.replace({ path: this.$route.path }).catch(() => {});
      this.resumeAfterPurchase();
      return;
    }
    // Back from a pack bought on the result page.
    if (this.$route.query.credits === "added") {
      this.creditsAdded = true;
      this.$router.replace({ path: this.$route.path }).catch(() => {});
      this.awaitPurchasedCredits();
    }
  },
  beforeDestroy() {
    this.persistSettings();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },
  methods: {
    persistSettings() {
      savedSettings.scale = this.scale;
      savedSettings.realism = this.realism;
      savedSettings.enhancementStrength = this.enhancementStrength;
      savedSettings.faceEnhancement = this.faceEnhancement;
      savedSettings.faceStrength = this.faceStrength;
      savedSettings.faceCreativity = this.faceCreativity;
      savedSettings.outputFormat = this.outputFormat;
      savedSettings.cropToFill = this.cropToFill;
    },

    // ---------------------------------------------------------------
    // File selection
    // ---------------------------------------------------------------
    onFileInputChange(event) {
      const files = Array.from(event.target.files || []);
      if (files.length) this.setSelectedFiles(files);
      // Allow re-selecting the same file (e.g. after clearing an error).
      event.target.value = "";
    },
    onDrop(event) {
      this.dragActive = false;
      if (this.uploading) return;
      const files = Array.from((event.dataTransfer && event.dataTransfer.files) || []);
      if (files.length) this.setSelectedFiles(files);
    },
    // One file keeps the classic single-image flow; several become a batch
    // queue (the server runs them under the plan's concurrency, FIFO).
    setSelectedFiles(files) {
      if (files.length === 1) {
        this.batchFiles = [];
        this.setSelectedFile(files[0]);
        return;
      }
      this.fileError = null;
      const allowed = ["image/jpeg", "image/png", "image/webp"];
      const MAX_BYTES = 25 * 1024 * 1024;
      const BATCH_CAP = 10;
      if (files.length > BATCH_CAP) {
        this.fileError = "Batches are limited to " + BATCH_CAP + " images at a time.";
        return;
      }
      for (const file of files) {
        if (allowed.indexOf(file.type) === -1) {
          this.fileError = file.name + ": only JPEG, PNG and WebP images are supported.";
          return;
        }
        if (file.size > MAX_BYTES) {
          this.fileError = file.name + " exceeds the 25MB limit.";
          return;
        }
      }
      this.outOfCreditsMessage = null;
      this.failureMessage = null;
      this.parkedImageId = null;
      this.selectedFile = null;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      this.batchFiles = files.map(file => ({ file, status: "ready", error: null }));
      // Representative estimate from the first image's size.
      this.srcWidth = null;
      this.srcHeight = null;
      this.probeDimensions(files[0]).then(dims => {
        if (dims) {
          this.srcWidth = dims.width;
          this.srcHeight = dims.height;
        }
        this.fetchEstimate();
      });
    },
    probeDimensions(file) {
      return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const probe = new Image();
        probe.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
        };
        probe.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        probe.src = url;
      });
    },
    batchStatusLabel(status) {
      const labels = {
        ready: "ready",
        checking: "pricing",
        uploading: "uploading",
        done: "queued",
        failed: "failed",
        needs_credits: "needs credits"
      };
      return labels[status] || status;
    },
    batchChipColor(status) {
      const colors = {
        checking: "primary",
        uploading: "primary",
        done: "success",
        failed: "error",
        needs_credits: "warning"
      };
      return colors[status] || undefined;
    },
    setSelectedFile(file) {
      this.fileError = null;
      this.batchFiles = [];
      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (allowed.indexOf(file.type) === -1) {
        this.fileError = "Only JPEG, PNG and WebP images are supported.";
        return;
      }
      const MAX_BYTES = 25 * 1024 * 1024;
      if (file.size > MAX_BYTES) {
        this.fileError = "Image exceeds the 25MB limit.";
        return;
      }

      this.outOfCreditsMessage = null;
      this.failureMessage = null;
      this.parkedImageId = null;
      this.selectedFile = file;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = URL.createObjectURL(file);
      // Pricing depends on the source size - read the dimensions locally
      // so the estimate is exact before anything uploads.
      this.srcWidth = null;
      this.srcHeight = null;
      const probe = new Image();
      probe.onload = () => {
        this.srcWidth = probe.naturalWidth;
        this.srcHeight = probe.naturalHeight;
        this.fetchEstimate();
      };
      probe.src = this.previewUrl;
    },

    // ---------------------------------------------------------------
    // Cost estimate
    // ---------------------------------------------------------------
    async fetchEstimate() {
      this.estimating = true;
      try {
        const params = { scale: this.scale, realism: this.realism };
        if (this.srcWidth && this.srcHeight) {
          params.width = this.srcWidth;
          params.height = this.srcHeight;
        }
        const response = await apiClient.get("/apps/upscale/estimate", {
          params
        });
        this.estimateInfo = response.data || null;
        return this.estimateInfo;
      } catch (e) {
        this.estimateInfo = null;
        return null;
      } finally {
        this.estimating = false;
      }
    },

    // ---------------------------------------------------------------
    // Upload + run → navigate to the image's own URL
    // ---------------------------------------------------------------
    async uploadAndRun() {
      if (this.uploading) return;
      if (this.batchFiles.length) {
        await this.runBatch();
        return;
      }
      const offeredTrial = !!(this.estimateInfo && this.estimateInfo.trial);
      this.uploading = true;
      this.outOfCreditsMessage = null;
      this.failureMessage = null;

      try {
        const estimate = this.estimating || !this.estimateInfo
          ? await this.fetchEstimate()
          : this.estimateInfo;
        if (!estimate) {
          this.failureMessage = "We couldn't check the conversion price. Please try again.";
          return;
        }
        // Keep the subscription entry point available even before file selection.
        if (!estimate.affordable) {
          this.showSubscribeDialog = true;
          return;
        }
        if (!this.selectedFile) {
          this.fileError = "Choose an image to continue.";
          return;
        }

        const formData = this.buildUploadForm(this.selectedFile);

        const response = await apiClient.post("/apps/upscale/images", formData, {
          headers: { "Content-Type": "multipart/form-data",
            ...workFundingHeaders(offeredTrial ? { trial: true } : estimate) }
        });
        this.persistSettings();
        this.$router.push(`/images/${response.data.id}`);
      } catch (e) {
        this.handleSubmitError(e);
      } finally {
        this.uploading = false;
      }
    },

    buildUploadForm(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("scale", String(this.scale));
      formData.append("realism", this.realism);
      formData.append("enhancement_strength", this.enhancementStrength);
      formData.append("face_enhancement", this.faceEnhancement ? "true" : "false");
      if (this.faceEnhancement) {
        formData.append("face_enhancement_strength", String(this.faceStrength));
        formData.append("face_enhancement_creativity", String(this.faceCreativity));
      }
      formData.append("output_format", this.outputFormat);
      formData.append("crop_to_fill", this.cropToFill ? "true" : "false");
      formData.append("run_now", "true");
      return formData;
    },

    // Batch: price and submit each image in turn. Every submission carries
    // its own estimate-derived funding header, so the first unaffordable
    // image stops the queue at the paywall instead of half-failing jobs.
    async runBatch() {
      this.uploading = true;
      this.outOfCreditsMessage = null;
      this.failureMessage = null;
      let submitted = 0;
      let halted = false;
      try {
        for (const entry of this.batchFiles) {
          if (entry.status === "done") continue; // partial re-run
          entry.status = "checking";
          const dims = await this.probeDimensions(entry.file);
          const params = { scale: this.scale, realism: this.realism };
          if (dims) {
            params.width = dims.width;
            params.height = dims.height;
          }
          let estimate = null;
          try {
            const response = await apiClient.get("/apps/upscale/estimate", { params });
            estimate = response.data || null;
          } catch (e) {
            estimate = null;
          }
          if (!estimate) {
            entry.status = "failed";
            entry.error = "price check failed";
            continue;
          }
          if (!estimate.affordable) {
            entry.status = "needs_credits";
            this.showSubscribeDialog = true;
            halted = true;
            break;
          }
          entry.status = "uploading";
          try {
            await apiClient.post("/apps/upscale/images", this.buildUploadForm(entry.file), {
              headers: { "Content-Type": "multipart/form-data",
                ...workFundingHeaders(estimate.trial ? { trial: true } : estimate) }
            });
            entry.status = "done";
            submitted += 1;
          } catch (e) {
            const status = e && e.response && e.response.status;
            if (status === 402) {
              entry.status = "needs_credits";
              this.showSubscribeDialog = true;
              halted = true;
              break;
            }
            entry.status = "failed";
            entry.error = "upload failed";
          }
        }
        this.persistSettings();
        const allDone = this.batchFiles.every(entry => entry.status === "done");
        if (submitted && allDone && !halted) {
          this.$router.push("/gallery");
        } else if (submitted) {
          this.failureMessage =
            submitted + " of " + this.batchFiles.length +
            " images are converting - watch them in your Gallery. The rest are listed above.";
        }
      } finally {
        this.uploading = false;
      }
    },

    async retryParked() {
      if (!this.parkedImageId) return;
      this.retrying = true;
      this.outOfCreditsMessage = null;
      try {
        await apiClient.post(`/apps/upscale/images/${this.parkedImageId}/run`, null,
          { headers: workFundingHeaders(this.estimateInfo) });
        this.persistSettings();
        this.$router.push(`/images/${this.parkedImageId}`);
      } catch (e) {
        this.handleSubmitError(e);
      } finally {
        this.retrying = false;
      }
    },

    // Credits from a result-page purchase arrive via the payment webhook,
    // usually within seconds: poll the balance briefly, then refresh the
    // estimate so the out-of-funds banner gives way to the normal flow.
    async awaitPurchasedCredits() {
      for (let attempt = 0; attempt < 10; attempt++) {
        await this.$store.dispatch("fetchCreditBalance").catch(() => {});
        const balance = this.$store.getters.creditBalance;
        if (balance && Number(balance.balance) > 0) {
          this.creditsArrived = true;
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 2500));
      }
      // Slower than ~25s: the confirmation still stands; the header and
      // estimate pick the credits up on their next refresh.
      this.creditsArrived = true;
      await this.fetchEstimate();
    },

    // Back from Stripe with a parked upload. The credits land via the
    // payment webhook, usually within seconds of the redirect - retry the
    // run until they do (402 = not yet), then open the result as if the
    // paywall had never interrupted.
    async resumeAfterPurchase() {
      this.resumingPurchase = true;
      this.outOfCreditsMessage = null;
      try {
        await this.fetchEstimate();
        for (let attempt = 0; attempt < 10; attempt++) {
          try {
            await apiClient.post(
              `/apps/upscale/images/${this.parkedImageId}/run`,
              null,
              { headers: workFundingHeaders(this.estimateInfo) }
            );
            this.$store.dispatch("fetchCreditBalance").catch(() => {});
            this.$router.push(`/images/${this.parkedImageId}`);
            return;
          } catch (e) {
            if (!(e.response && e.response.status === 402)) {
              this.handleSubmitError(e);
              return;
            }
            await new Promise(resolve => setTimeout(resolve, 2500));
            await this.fetchEstimate();
          }
        }
        // Webhook slower than ~25s: keep the manual path, nothing is lost.
        this.$store.dispatch("fetchCreditBalance").catch(() => {});
        this.outOfCreditsMessage =
          "Payment received - your credits are on their way. " +
          "Hit Retry now once they arrive.";
      } finally {
        this.resumingPurchase = false;
      }
    },

    handleSubmitError(e) {
      if (e.workPending) {
        this.failureMessage = "We couldn't confirm your submission yet. Try again with the same image and settings to resume it safely.";
        return;
      }
      const detail = e.response && e.response.data && e.response.data.detail;
      if (e.response && e.response.status === 409 && detail && detail.code === "allowance_unavailable") {
        this.failureMessage = detail.message;
        this.fetchEstimate();
        return;
      }
      if (e.response && e.response.status === 402) {
        this.showSubscribeDialog = true;
        const detail = e.response.data && e.response.data.detail;
        this.outOfCreditsMessage =
          (detail && (detail.message || detail)) ||
          "You're out of credits. Subscribe to continue.";
        // The server preserves the upload as needs_credits; remember it so
        // "Retry now" can resume without re-uploading.
        if (e.response.data && e.response.data.id) {
          this.parkedImageId = e.response.data.id;
        }
      } else {
        this.failureMessage = (detail && (detail.message || detail)) ||
          "Something went wrong while starting the enhancement. Please try again.";
      }
    }
  }
};
</script>

<style scoped>
.upload-column {
  max-width: 760px;
  margin: 0 auto;
}

.upscale-subtitle {
  color: var(--text-secondary);
}

.dropzone {
  border: 1.6px dashed var(--border-input);
  border-radius: 10px;
  min-height: 300px;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.dropzone--active {
  border-color: var(--accent, #a855f7);
  background: var(--overlay-light);
}

.dropzone--has-file {
  padding: 12px;
  min-height: 0;
}

.dropzone-input {
  display: none;
}

.dropzone-text {
  font-weight: 600;
}

.dropzone-hint {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
}

.dropzone-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
}

.error-text {
  color: var(--error, #f87171);
  font-size: var(--font-size-xs);
}

.batch-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.batch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 8px;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  font-size: var(--font-size-xs);
}

.batch-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.estimate-row {
  min-height: 20px;
}

.trial-note {
  color: var(--success, #34d399);
}

.estimate-short {
  color: var(--warning, #fbbf24);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.realism-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

@media (max-width: 500px) {
  .realism-grid {
    grid-template-columns: 1fr;
  }
}

.realism-card {
  border: 1.4px solid var(--border-input);
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.realism-card:hover {
  border-color: var(--accent, #a855f7);
}

.realism-card--selected {
  border-color: var(--accent, #a855f7);
  background: var(--overlay-light);
  box-shadow: 0 0 10px rgba(168, 85, 247, 0.25);
}

.realism-card--disabled {
  opacity: 0.6;
  cursor: default;
  pointer-events: none;
}

.realism-card-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.realism-card-desc {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
}

.output-size-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  font-weight: 600;
}

.premium-hint {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.option-hint {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
}

.advanced-panels {
  background: transparent;
}

.advanced-panels .v-expansion-panel {
  background: transparent !important;
}

.advanced-content >>> .v-expansion-panel-content__wrap {
  padding-left: 0;
  padding-right: 0;
}
</style>
